import React, { useId } from "react";

import { EmptyState } from "./EmptyState";
import { ErrorState } from "./ErrorState";
import { Skeleton } from "./Skeleton";

import { useIntl } from "react-intl";

/*
  Panel shell. Takes a heading, an optional caption and children; knows nothing about data.

  - status="loading" renders the skeleton at `minHeight`;
  - `error` (a message) renders the compact error with `onRetry`;
  - `isEmpty` renders `emptyMessage` instead of the children.
  The heading, caption and actions stay put in every state, so the panel does not jump.
*/
export const Panel = ({
  title, caption, actions, status, isEmpty = false, emptyMessage, error, onRetry, flush = false, minHeight, children
}) => {
  const intl = useIntl();
  const headingId = useId();

  if (status === "loading") { return <Skeleton height={minHeight} />; }

  let body = children;
  if (error) {
    body = (
      <ErrorState
        title={intl.formatMessage({ id: "ff_dashboard.error.panel", defaultMessage: "This panel could not be loaded." })}
        message={typeof error === "string" ? error : ""}
        onRetry={onRetry}
      />
    );
  } else if (isEmpty) {
    body = <EmptyState>{emptyMessage}</EmptyState>;
  }

  return (
    <section className={`ff-panel${flush ? " ff-flush" : ""}`} aria-labelledby={headingId} style={{ minHeight }}>
      <div className="ff-panel-head">
        <h2 id={headingId}>{title}</h2>
        {caption && <span className="ff-cap">{caption}</span>}
        {actions && <><span className="ff-spacer" />{actions}</>}
      </div>
      {body}
    </section>
  );
};
